import React, { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { getCurrentWeather } from "../lib/ambientWeather";
import { refitModel } from "../lib/modelData";
import { localDateISO, localTimeHHMM } from "../lib/time";

const NUMERIC_FIELDS = [
  { key: "pH", label: "pH", step: 0.1, placeholder: "7.4" },
  { key: "fc", label: "Free Chlorine (ppm)", step: 0.1, placeholder: "2.0" },
  { key: "tc", label: "Total Chlorine (ppm)", step: 0.1, placeholder: "2.0" },
  { key: "ta", label: "Total Alkalinity (ppm)", step: 1, placeholder: "100" },
  { key: "ch", label: "Calcium Hardness (ppm)", step: 1, placeholder: "300" },
  { key: "cya", label: "CYA / Stabilizer (ppm)", step: 1, placeholder: "40" },
  { key: "waterTemp", label: "Water Temp (°F)", step: 1, placeholder: "84" },
];

const PANELS = [
  { id: "quick", label: "2-panel", fields: ["fc", "tc", "pH", "waterTemp"] },
  { id: "full", label: "Full panel", fields: NUMERIC_FIELDS.map((f) => f.key) },
];
const PANEL_STORAGE_KEY = "piq-log-panel";

// How FC/TC were read. Saved on each reading so the model can weight it.
const KITS = [
  { id: "color", label: "Color blocks" },
  { id: "titration", label: "Drop count" },
];
const KIT_STORAGE_KEY = "piq-chlorine-kit";
const CHLORINE_KEYS = ["fc", "tc"];

// Comparator blocks plus the in-between reads (shown faded).
const COLOR_STEPS = [0, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 10];
const COLOR_BLOCKS = new Set([0, 1, 2, 3, 5, 7.5, 10]);

function loadChoice(key, allowed, fallback) {
  try {
    const v = localStorage.getItem(key);
    return allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function remember(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function ChipPicker({ label, value, onChange }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <div style={styles.chipRow} role="radiogroup" aria-label={label}>
        {COLOR_STEPS.map((v) => {
          const selected = value !== "" && Number(value) === v;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(selected ? "" : String(v))}
              style={{
                ...styles.chip,
                ...(COLOR_BLOCKS.has(v) ? {} : styles.chipBetween),
                ...(selected ? styles.chipActive : {}),
              }}
            >
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function LogEntry({ onSaved }) {
  const [values, setValues] = useState({
    pH: "",
    fc: "",
    tc: "",
    ta: "",
    ch: "",
    cya: "",
    waterTemp: "",
    salt: "",
  });
  const [date, setDate] = useState(localDateISO());
  const [time, setTime] = useState(localTimeHHMM());
  const [testedBy, setTestedBy] = useState("Ryan");
  const [notes, setNotes] = useState("");
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [panel, setPanel] = useState(() => loadChoice(PANEL_STORAGE_KEY, ["quick", "full"], "full"));
  const panelFields = PANELS.find((p) => p.id === panel).fields;
  const [kit, setKit] = useState(() => loadChoice(KIT_STORAGE_KEY, ["color", "titration"], "color"));

  const choosePanel = (id) => {
    setPanel(id);
    remember(PANEL_STORAGE_KEY, id);
  };

  // Clear FC/TC on a kit switch so a value from the other kit's scale
  // can't be saved without being visible.
  const chooseKit = (id) => {
    if (id === kit) return;
    setKit(id);
    remember(KIT_STORAGE_KEY, id);
    setValues((prev) => ({ ...prev, fc: "", tc: "" }));
  };

  const chlorineFields = NUMERIC_FIELDS.filter(
    (f) => CHLORINE_KEYS.includes(f.key) && panelFields.includes(f.key)
  );
  const gridFields = NUMERIC_FIELDS.filter(
    (f) => panelFields.includes(f.key) && !(kit === "color" && CHLORINE_KEYS.includes(f.key))
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  // Prefill water temp from the previous reading.
  useEffect(() => {
    if (!db) return undefined;
    const q = query(collection(db, "logs"), orderBy("createdAt", "desc"), limit(1));
    const unsub = onSnapshot(q, (snap) => {
      const last = snap.empty ? null : snap.docs[0].data();
      if (last && last.waterTemp !== undefined && last.waterTemp !== null) {
        setValues((prev) =>
          prev.waterTemp === "" || prev.waterTemp === undefined
            ? { ...prev, waterTemp: String(last.waterTemp) }
            : prev
        );
      }
    });
    return unsub;
  }, []);

  const fcEntered = values.fc === "" ? null : Number(values.fc);
  const tcEntered = values.tc === "" ? null : Number(values.tc);
  const tcBelowFc =
    fcEntered !== null && tcEntered !== null && tcEntered < fcEntered;

  const handleChange = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!db) {
      setError("Firestore is not available (Firebase not configured).");
      return;
    }

    setSaving(true);
    try {
      const toNumberOrNull = (v) =>
        v === "" || v === undefined || v === null ? null : Number(v);

      // Snapshot current weather at the moment of logging so the Sheets sync
      // (Cloud Function) can fill in the warranty sheet's weather columns
      // (M-R) with what the conditions actually were at this reading, not
      // just whatever the dashboard happens to show later. Best-effort: if
      // the Worker isn't configured or the fetch fails, weather is just
      // omitted from the doc and the Sheet's M-R cells are left blank.
      const weather = await getCurrentWeather().catch(() => ({
        available: false,
      }));

      // Tests outside the chosen panel are saved as not tested (null), even
      // if a value was typed before switching panels.
      const tested = (key) =>
        panelFields.includes(key) ? toNumberOrNull(values[key]) : null;
      const reading = {
        date,
        time,
        pH: tested("pH"),
        fc: tested("fc"),
        tc: tested("tc"),
        ta: tested("ta"),
        ch: tested("ch"),
        cya: tested("cya"),
        salt: panel === "full" ? toNumberOrNull(values.salt) : null,
        waterTemp: tested("waterTemp"),
      };
      reading.chlorineKit = reading.fc !== null || reading.tc !== null ? kit : null;

      await addDoc(collection(db, "logs"), {
        ...reading,
        testedBy: testedBy || "Ryan",
        notes: notes || "",
        weatherAirTempF: weather.available ? weather.tempf ?? null : null,
        weatherHumidityPct: weather.available ? weather.humidity ?? null : null,
        weatherUvIndex: weather.available ? weather.uv ?? null : null,
        weatherRainIn: weather.available ? weather.dailyrainin ?? null : null,
        weatherSolarRad: weather.available
          ? weather.solarradiation ?? null
          : null,
        weatherWindMph: weather.available
          ? weather.windspeedmph ?? null
          : null,
        createdAt: serverTimestamp(),
      });

      // Recalibrate with the new reading. Runs in the background (it fetches
      // station history) and never blocks the save.
      refitModel().catch((err) =>
        console.warn("Model refit failed (non-fatal):", err)
      );

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        if (onSaved) onSaved();
      }, 1200);
    } catch (err) {
      setError(err.message || "Failed to save log entry.");
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div style={styles.confirmationCard}>
        <div style={{ fontSize: 40 }}>✅</div>
        <div style={{ fontWeight: 700, fontSize: 18, marginTop: 8 }}>
          Logged!
        </div>
        <div style={{ color: "var(--piq-text-muted)", marginTop: 4 }}>
          Returning to dashboard…
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={styles.card}>
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Time</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              style={styles.input}
              required
            />
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.panelHeader}>
          <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>Readings</div>
          <div style={styles.panelToggle} role="group" aria-label="Test panel">
            {PANELS.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={panel === p.id}
                onClick={() => choosePanel(p.id)}
                style={{
                  ...styles.panelButton,
                  ...(panel === p.id ? styles.panelButtonActive : {}),
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {chlorineFields.length > 0 && (
          <div style={styles.kitRow}>
            <span style={styles.kitLabel}>Chlorine test</span>
            <div style={styles.panelToggle} role="group" aria-label="Chlorine test kit">
              {KITS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  aria-pressed={kit === k.id}
                  onClick={() => chooseKit(k.id)}
                  style={{
                    ...styles.panelButton,
                    ...(kit === k.id ? styles.panelButtonActive : {}),
                  }}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {kit === "color" && chlorineFields.length > 0 && (
          <div style={styles.chipSection}>
            {chlorineFields.map((f) => (
              <ChipPicker
                key={f.key}
                label={f.label}
                value={values[f.key]}
                onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
              />
            ))}
            <div style={styles.helperText}>Faded values are between two color blocks.</div>
          </div>
        )}
        <div style={styles.grid}>
          {gridFields.map((f) => {
            const drop = kit === "titration" && CHLORINE_KEYS.includes(f.key);
            return (
              <div style={styles.field} key={f.key}>
                <label style={styles.label}>{f.label}</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step={drop ? 0.2 : f.step}
                  placeholder={drop ? "2.4" : f.placeholder}
                  value={values[f.key]}
                  onChange={handleChange(f.key)}
                  style={styles.bigInput}
                />
              </div>
            );
          })}
        </div>
        {tcBelowFc && (
          <div style={styles.warningBanner}>
            Total chlorine can't be lower than free chlorine, so one of these
            readings is off. Worth re-testing; you can still save.
          </div>
        )}
      </div>

      {panel === "full" && (
        <div style={styles.card}>
          <button
            type="button"
            onClick={() => setOptionalOpen((o) => !o)}
            style={styles.disclosureButton}
          >
            {optionalOpen ? "▾" : "▸"} Optional / SWG (Salt)
          </button>
          {optionalOpen && (
            <div style={{ marginTop: 12 }}>
              <div style={styles.field}>
                <label style={styles.label}>Salt (ppm)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step={10}
                  placeholder="n/a — no SWG on this pool"
                  value={values.salt}
                  onChange={handleChange("salt")}
                  style={styles.input}
                />
                <div style={styles.helperText}>
                  This pool has no salt water generator. Kept for parity with
                  the warranty sheet only — not warranty-flagged.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.field}>
          <label style={styles.label}>Tested By</label>
          <input
            type="text"
            value={testedBy}
            onChange={(e) => setTestedBy(e.target.value)}
            style={styles.input}
          />
        </div>
        <div style={{ ...styles.field, marginTop: 12 }}>
          <label style={styles.label}>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={styles.textarea}
            rows={3}
            placeholder="Optional notes (backwash, algae, added chemicals, etc.)"
          />
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      <button type="submit" disabled={saving} style={styles.submitButton}>
        {saving ? "Saving…" : "Save Log Entry"}
      </button>
    </form>
  );
}

const styles = {
  card: {
    background: "var(--piq-card-bg)",
    border: "1px solid var(--piq-border)",
    borderRadius: "var(--piq-radius)",
    padding: 18,
    boxShadow: "var(--piq-shadow)",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--piq-text-muted)",
    marginBottom: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  row: {
    display: "flex",
    gap: 14,
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
  },
  kitRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
  },
  kitLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--piq-text-muted)",
  },
  chipSection: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 14,
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  chip: {
    minWidth: 44,
    padding: "10px 6px",
    border: "1px solid var(--piq-border)",
    borderRadius: "var(--piq-radius)",
    background: "var(--piq-bg)",
    color: "var(--piq-text)",
    fontSize: 15,
    fontWeight: 600,
    fontFamily: "var(--piq-font-mono)",
    cursor: "pointer",
  },
  chipBetween: {
    color: "var(--piq-text-muted)",
    fontWeight: 500,
    fontSize: 13,
  },
  chipActive: {
    background: "var(--piq-primary)",
    borderColor: "var(--piq-primary)",
    color: "var(--piq-on-accent)",
  },
  panelToggle: {
    display: "flex",
    gap: 4,
    background: "var(--piq-bg)",
    border: "1px solid var(--piq-border)",
    borderRadius: "var(--piq-radius)",
    padding: 3,
  },
  panelButton: {
    border: "1px solid transparent",
    background: "none",
    borderRadius: "var(--piq-radius)",
    padding: "6px 10px",
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "var(--piq-font-mono)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--piq-text-muted)",
    cursor: "pointer",
  },
  panelButtonActive: {
    background: "var(--piq-card-bg)",
    borderColor: "var(--piq-primary)",
    color: "var(--piq-primary)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--piq-text-muted)",
  },
  input: {
    border: "1px solid var(--piq-border)",
    borderRadius: "var(--piq-radius)",
    background: "var(--piq-card-bg)",
    color: "var(--piq-text)",
    padding: "10px 12px",
    fontSize: 16,
  },
  bigInput: {
    width: "100%",
    minWidth: 0,
    border: "1px solid var(--piq-border)",
    borderRadius: "var(--piq-radius)",
    background: "var(--piq-bg)",
    padding: "14px 12px",
    fontSize: 20,
    fontWeight: 600,
    fontFamily: "var(--piq-font-mono)",
    color: "var(--piq-primary)",
  },
  textarea: {
    border: "1px solid var(--piq-border)",
    borderRadius: "var(--piq-radius)",
    background: "var(--piq-card-bg)",
    color: "var(--piq-text)",
    padding: "10px 12px",
    fontSize: 15,
    fontFamily: "inherit",
    resize: "vertical",
  },
  disclosureButton: {
    background: "none",
    border: "none",
    padding: 0,
    fontSize: 14,
    fontWeight: 700,
    color: "var(--piq-text-muted)",
    cursor: "pointer",
  },
  helperText: {
    fontSize: 12,
    color: "var(--piq-text-muted)",
    marginTop: 4,
  },
  warningBanner: {
    marginTop: 14,
    background: "var(--piq-yellow-bg)",
    color: "var(--piq-yellow)",
    borderRadius: "var(--piq-radius)",
    padding: "10px 12px",
    fontSize: 13,
    lineHeight: 1.45,
  },
  errorBanner: {
    background: "var(--piq-red-bg)",
    color: "var(--piq-red)",
    borderRadius: "var(--piq-radius)",
    padding: "10px 14px",
    fontSize: 14,
  },
  submitButton: {
    background: "var(--piq-primary)",
    color: "var(--piq-on-accent)",
    border: "none",
    borderRadius: "var(--piq-radius)",
    padding: "16px",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
  },
  confirmationCard: {
    textAlign: "center",
    padding: 48,
  },
};
